/**
 * Admin feature management hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';
import { BASE_IMAGE_KEY } from './useBaseImageInfo';
import type { Feature, SyncResult } from '../schemas/admin.schemas';

const ADMIN_FEATURES_KEY = ['admin-features'];

export function useAdminFeatures(includeInactive = false) {
  return useQuery<Feature[]>({
    queryKey: [...ADMIN_FEATURES_KEY, { includeInactive }],
    queryFn: () => adminApi.listFeatures(includeInactive),
  });
}

export function useCreateFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      feature: Omit<Feature, 'id' | 'created_at' | 'updated_at' | 'is_active'>,
    ) => adminApi.createFeature(feature),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_FEATURES_KEY });
    },
  });
}

export function useUpdateFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      featureId,
      updates,
    }: {
      featureId: string;
      updates: Record<string, unknown>;
    }) => adminApi.updateFeature(featureId, updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_FEATURES_KEY });
    },
  });
}

export function useDeleteFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (featureId: string) => adminApi.deleteFeature(featureId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_FEATURES_KEY });
    },
  });
}

export function useSyncFeatures() {
  const queryClient = useQueryClient();

  return useMutation<SyncResult>({
    mutationFn: () => adminApi.syncFeatures(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_FEATURES_KEY });
      void queryClient.invalidateQueries({ queryKey: BASE_IMAGE_KEY });
    },
  });
}
